import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useState, type ReactElement } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { NameField, type FieldServerError } from './NameField';
import { addCatalogAlias } from '../../../api/admin-vehicle-catalog';
import { aliasFormSchema, type AliasFormValues } from '../_lib/catalogForms';
import type { UseCatalogWriteResult } from '../_lib/useCatalogWrite';

/**
 * Inline «+ Алиас» form (AUT-437): `POST /admin/vehicle-catalog/aliases`
 * (kind `spelling`). A 409 names the entry that already answers to it.
 * The runner refetches the make afterwards — the owner's `updatedAt` moved.
 */
export interface AliasAddFormProps {
  makeId: string;
  modelId?: string;
  write: UseCatalogWriteResult;
  onOpenMake: (makeId: string) => void;
  onDone: () => void;
}

export const AliasAddForm = ({
  makeId,
  modelId,
  write,
  onOpenMake,
  onDone,
}: AliasAddFormProps): ReactElement => {
  const { t } = useTranslation();
  const [serverError, setServerError] = useState<FieldServerError | null>(null);
  const form = useForm<AliasFormValues>({
    resolver: zodResolver(aliasFormSchema),
    defaultValues: { alias: '' },
  });

  const submit = async ({ alias }: AliasFormValues): Promise<void> => {
    const body = { makeId, alias, ...(modelId ? { modelId } : {}) };
    const outcome = await write.run(() => addCatalogAlias(body), {
      successKey: 'vehicleCatalog.aliases.added',
    });
    if (outcome.ok) onDone();
    else if (outcome.error.kind === 'conflict' || outcome.error.kind === 'tooLong') {
      setServerError(outcome.error);
    }
  };

  return (
    <Box
      component="form"
      noValidate
      onSubmit={(e) => {
        e.stopPropagation();
        void form.handleSubmit(submit)(e);
      }}
      sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', mt: 1 }}
    >
      <Box sx={{ flex: 1 }}>
        <Controller
          control={form.control}
          name="alias"
          render={({ field, fieldState }) => (
            <NameField
              {...field}
              onChange={(v) => {
                setServerError(null);
                field.onChange(v);
              }}
              label={t('vehicleCatalog.aliases.addLabel')}
              errorKey={fieldState.error?.message}
              serverError={serverError}
              onOpenMake={onOpenMake}
              currentMakeId={makeId}
              disabled={write.busy}
              focusOnMount
            />
          )}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 1, pb: 3 }}>
        <Button onClick={onDone} disabled={write.busy}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="contained" disabled={write.busy} loading={write.busy}>
          {t('vehicleCatalog.aliases.addSubmit')}
        </Button>
      </Box>
    </Box>
  );
};
